import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Save,
  CreditCard,
  Landmark,
  Receipt,
  Zap,
  Briefcase,
  Gift,
  ShoppingBag,
  Sparkles,
  Paperclip,
  Trash2,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Copy,
  Check,
  Calendar,
  MapPin,
  FileText,
  AlertCircle,
  Share2,
  Download,
  Plus,
  Clock,
} from 'lucide-react';

export const CARD_TYPES = [
  { id: 'credit', name: 'Credit Card', icon: CreditCard },
  { id: 'debit', name: 'Debit Card', icon: Landmark },
  { id: 'prepaid', name: 'Prepaid Card', icon: Receipt },
  { id: 'virtual', name: 'Virtual Card', icon: Zap },
  { id: 'business', name: 'Business Card', icon: Briefcase },
  { id: 'gift', name: 'Gift Card', icon: Gift },
  { id: 'store', name: 'Store / Retail Card', icon: ShoppingBag },
  { id: 'custom', name: 'Custom Card', icon: Sparkles },
];

export const CARD_THEMES = [
  { id: 'sapphire', name: 'Sapphire Royal', gradient: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #2563eb 100%)', text: '#ffffff' },
  { id: 'onyx', name: 'Midnight Onyx', gradient: 'linear-gradient(135deg, #09090b 0%, #18181b 50%, #27272a 100%)', text: '#ffffff' },
  { id: 'emerald', name: 'Emerald Luxury', gradient: 'linear-gradient(135deg, #022c22 0%, #065f46 50%, #059669 100%)', text: '#ffffff' },
  { id: 'gold', name: 'Amex Gold', gradient: 'linear-gradient(135deg, #451a03 0%, #b45309 50%, #f59e0b 100%)', text: '#ffffff' },
  { id: 'platinum', name: 'Platinum Steel', gradient: 'linear-gradient(135deg, #1e293b 0%, #475569 50%, #94a3b8 100%)', text: '#ffffff' },
  { id: 'sunset', name: 'Sunset Coral', gradient: 'linear-gradient(135deg, #431407 0%, #9a3412 50%, #ea580c 100%)', text: '#ffffff' },
  { id: 'violet', name: 'Cyber Violet', gradient: 'linear-gradient(135deg, #2e1065 0%, #581c87 50%, #7c3aed 100%)', text: '#ffffff' },
  { id: 'rose', name: 'Rose Gold', gradient: 'linear-gradient(135deg, #500724 0%, #831843 50%, #db2777 100%)', text: '#ffffff' },
];

// Helper: detect card network from number
export const detectCardNetwork = (rawNumber = '') => {
  const clean = rawNumber.replace(/\D/g, '');
  if (/^4/.test(clean)) return { name: 'Visa', code: 'visa', color: '#1a1f71' };
  if (/^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[0-1]|2720)/.test(clean))
    return { name: 'Mastercard', code: 'mastercard', color: '#eb001b' };
  if (/^3[47]/.test(clean)) return { name: 'American Express', code: 'amex', color: '#006fcf' };
  if (/^(6011|622(12[6-9]|1[3-9]|[2-8]|9[0-1]|92[0-5])|64[4-9]|65)/.test(clean))
    return { name: 'Discover', code: 'discover', color: '#ff6000' };
  if (/^35(2[89]|[3-8][0-9])/.test(clean)) return { name: 'JCB', code: 'jcb', color: '#0e4c92' };
  if (/^3(0[0-5]|[68])/.test(clean)) return { name: 'Diners Club', code: 'diners', color: '#004a97' };
  return { name: 'Card', code: 'generic', color: '#6366f1' };
};

// Auto-format card number with 4-digit spacing
const formatCardNumberString = (value) => {
  const clean = value.replace(/\D/g, '').substring(0, 19);
  return clean.replace(/(\d{4})(?=\d)/g, '$1 ');
};

export default function CardEditorModal({
  open,
  card, // existing card or null
  categories = [],
  onClose,
  onSave,
  hasEditPermission = true,
  onOpenTemplates,
}) {
  const [activeTab, setActiveTab] = useState('card'); // 'card' | 'address' | 'attachments' | 'security'

  // Primary fields
  const [cardName, setCardName] = useState('');
  const [cardType, setCardType] = useState('credit');
  const [issuer, setIssuer] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('12');
  const [expiryYear, setExpiryYear] = useState(String(new Date().getFullYear() + 3));
  const [cvv, setCvv] = useState('');
  const [pin, setPin] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [theme, setTheme] = useState('sapphire');
  const [category, setCategory] = useState('Finance');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [notes, setNotes] = useState('');

  // Sensitive field reveal states in editor
  const [showNumber, setShowNumber] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Billing address
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('United States');

  // Attachments
  const [attachments, setAttachments] = useState([]);

  // Security Lock & PIN
  const [isLocked, setIsLocked] = useState(false);
  const [lockPin, setLockPin] = useState('');

  // Sharing
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('VIEW');
  const [sharesList, setSharesList] = useState([]);

  // Status
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const autoHideTimerRef = useRef(null);

  // Lock body scroll on mobile when editor is open
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  // Initialize form
  useEffect(() => {
    if (!open) {
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      return;
    }

    if (card) {
      setCardName(card.cardName || card.title || '');
      setCardType(card.cardType || 'credit');
      const f = card.fields || card;
      setIssuer(f.issuer || '');
      setCardholderName(f.cardholderName || '');
      setCardNumber(f.cardNumber || '');
      setExpiryMonth(f.expiryMonth || '12');
      setExpiryYear(f.expiryYear || String(new Date().getFullYear() + 3));
      setCvv(f.cvv || '');
      setPin(f.pin || '');
      setCurrency(f.currency || 'USD');
      setTheme(f.theme || card.theme || 'sapphire');
      setCategory(card.category || 'Finance');
      setAccountNumber(f.accountNumber || card.accountNumber || '');
      setIfscCode(f.ifscCode || card.ifscCode || '');
      setTags(Array.isArray(card.tags) ? card.tags : []);
      setNotes(f.notes || card.notes || '');

      const addr = f.billingAddress || {};
      setStreet(addr.street || '');
      setCity(addr.city || '');
      setStateProvince(addr.state || addr.stateProvince || '');
      setPostalCode(addr.postalCode || '');
      setCountry(addr.country || 'United States');

      setAttachments(Array.isArray(card.attachments) ? card.attachments : []);
      setIsLocked(Boolean(card.isLocked));
      setLockPin(card.lockPin || '');
      setSharesList(Array.isArray(card.shares) ? card.shares : []);
    } else {
      setCardName('');
      setCardType('credit');
      setIssuer('');
      setCardholderName('');
      setCardNumber('');
      setExpiryMonth('12');
      setExpiryYear(String(new Date().getFullYear() + 3));
      setCvv('');
      setPin('');
      setCurrency('USD');
      setTheme('sapphire');
      setCategory(categories[0]?.name || 'Finance');
      setAccountNumber('');
      setIfscCode('');
      setTags([]);
      setNotes('');
      setStreet('');
      setCity('');
      setStateProvince('');
      setPostalCode('');
      setCountry('United States');
      setAttachments([]);
      setIsLocked(false);
      setLockPin('');
      setSharesList([]);
    }

    setShowNumber(false);
    setShowCvv(false);
    setShowPin(false);
    setError('');
    setActiveTab('card');
  }, [open, card, categories]);

  // Detected network
  const network = detectCardNetwork(cardNumber);

  // Number input formatter
  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumberString(e.target.value);
    setCardNumber(formatted);
  };

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Tag management
  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const clean = tagInput.trim().toLowerCase().replace(/^#/, '');
      if (clean && !tags.includes(clean)) {
        setTags([...tags, clean]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter((t) => t !== tag));
  };

  // Attachments upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`File ${file.name} exceeds 10MB limit.`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const newAtt = {
          id: `att_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
          name: file.name,
          size: file.size,
          type: file.type,
          dataUrl: reader.result,
          uploadedAt: new Date().toISOString(),
        };
        setAttachments((prev) => [...prev, newAtt]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const handleRemoveAttachment = (attId) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attId));
  };

  // Collaborator sharing
  const handleAddShare = (e) => {
    e.preventDefault();
    if (!shareRecipientEmail || !shareRecipientEmail.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    if (sharesList.some((s) => s.email.toLowerCase() === shareRecipientEmail.toLowerCase())) {
      setError('This user is already added.');
      return;
    }
    const newShare = {
      id: `share_${Date.now()}`,
      email: shareRecipientEmail.trim().toLowerCase(),
      permission: sharePermission,
      sharedAt: new Date().toISOString(),
    };
    setSharesList([...sharesList, newShare]);
    setShareRecipientEmail('');
    setError('');
  };

  const handleRemoveShare = (id) => {
    setSharesList(sharesList.filter((s) => s.id !== id));
  };

  // Submit & Save
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cardName.trim()) {
      setError('Card Name or Nickname is required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const cardPayload = {
        cardName: cardName.trim(),
        title: cardName.trim(),
        cardType,
        category,
        tags,
        theme,
        fields: {
          issuer: issuer.trim(),
          cardholderName: cardholderName.trim(),
          cardNumber: cardNumber.trim(),
          expiryMonth,
          expiryYear,
          cvv: cvv.trim(),
          pin: pin.trim(),
          currency,
          theme,
          accountNumber: accountNumber.trim(),
          ifscCode: ifscCode.trim().toUpperCase(),
          billingAddress: {
            street: street.trim(),
            city: city.trim(),
            state: stateProvince.trim(),
            postalCode: postalCode.trim(),
            country: country.trim(),
          },
          notes: notes.trim(),
        },
        attachments,
        isLocked,
        lockPin: isLocked ? lockPin : '',
        shares: sharesList,
        updated_at: new Date().toISOString(),
      };

      await onSave(cardPayload);
      onClose();
    } catch (err) {
      console.error('Failed to save card:', err);
      setError(err.message || 'Error encrypting and saving payment card.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="card-drawer-wrapper">
      <div
        className="card-drawer-overlay animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="card-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Add or Edit Payment Card"
      >
        {/* Drawer Header */}
        <div className="card-drawer-header">
          <div className="card-drawer-header-left">
            <div
              className="card-drawer-icon-badge"
              style={{ background: 'rgba(59, 130, 246, 0.14)', color: '#3b82f6' }}
            >
              <CreditCard size={20} />
            </div>
            <div className="card-drawer-titles">
              <h2 className="card-drawer-title">
                {card ? 'Edit Payment Card' : 'Add New Payment Card'}
              </h2>
              <p className="card-drawer-subtitle">
                Encrypted payment card credentials & details
              </p>
            </div>
          </div>

          <div className="card-drawer-header-actions">
            {!card && onOpenTemplates && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ fontSize: '0.74rem', padding: '5px 9px' }}
                onClick={onOpenTemplates}
                title="Choose from 8 card templates"
              >
                <Sparkles size={13} className="text-primary" />
                <span>Templates</span>
              </button>
            )}
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ margin: '10px 16px 0', padding: '8px 12px', fontSize: '0.78rem' }}>
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        {/* Tab Navigation - iOS Segmented Control */}
        <div style={{ padding: '12px 18px 0' }}>
          <div className="card-segmented-control" style={{ margin: 0 }}>
            <button
              type="button"
              className={`card-segmented-btn ${activeTab === 'card' ? 'active' : ''}`}
              onClick={() => setActiveTab('card')}
            >
              <CreditCard size={13} />
              <span>Card Details</span>
            </button>
            <button
              type="button"
              className={`card-segmented-btn ${activeTab === 'address' ? 'active' : ''}`}
              onClick={() => setActiveTab('address')}
            >
              <MapPin size={13} />
              <span>Billing Address</span>
            </button>
            <button
              type="button"
              className={`card-segmented-btn ${activeTab === 'attachments' ? 'active' : ''}`}
              onClick={() => setActiveTab('attachments')}
            >
              <Paperclip size={13} />
              <span>Scans {attachments.length > 0 ? `(${attachments.length})` : ''}</span>
            </button>
            <button
              type="button"
              className={`card-segmented-btn ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <Lock size={13} />
              <span>Security {sharesList.length > 0 ? `(${sharesList.length})` : ''}</span>
            </button>
          </div>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="card-drawer-body">
            {activeTab === 'card' && (
              <div className="identity-form-sections">
                {/* Live Card Mockup Preview */}
                <div className={`digital-card-hero theme-${theme}`}>
                  <div className="card-hero-header">
                    <span className="card-hero-issuer">{issuer || 'BANK / ISSUER'}</span>
                    <span className="card-hero-network-badge">{network.name}</span>
                  </div>
                  <div className="card-hero-chip-row">
                    <div className="card-hero-emv-chip"></div>
                    <div className="card-hero-contactless">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <div className="card-hero-number">
                    {cardNumber ? (showNumber ? cardNumber : `•••• •••• •••• ${cardNumber.replace(/\s+/g, '').slice(-4) || '••••'}`) : '•••• •••• •••• ••••'}
                  </div>
                  <div className="card-hero-footer">
                    <div>
                      <span className="card-hero-sublabel">CARDHOLDER</span>
                      <span className="card-hero-holder-name">
                        {cardholderName.toUpperCase() || 'YOUR NAME'}
                      </span>
                    </div>
                    <div>
                      <span className="card-hero-sublabel">EXPIRES</span>
                      <span className="card-hero-exp-date">
                        {expiryMonth}/{expiryYear.slice(-2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Section 1: Card Identification */}
                <div className="identity-form-section">
                  <h4 className="identity-section-heading">Card Identification & Classification</h4>
                  <div className="grid-2-col">
                    <div className="form-group">
                      <label className="form-label">
                        Card Name / Nickname <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. Chase Sapphire Preferred or Work Amex"
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Card Type</label>
                      <select
                        className="form-input"
                        value={cardType}
                        onChange={(e) => setCardType(e.target.value)}
                      >
                        {CARD_TYPES.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid-3-col" style={{ marginTop: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Bank / Card Issuer</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. Chase, Bank of America, Citi"
                        value={issuer}
                        onChange={(e) => setIssuer(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <select
                        className="form-input"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                      >
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                        <option value="CAD">CAD ($)</option>
                        <option value="AUD">AUD ($)</option>
                        <option value="JPY">JPY (¥)</option>
                        <option value="INR">INR (₹)</option>
                        <option value="CHF">CHF (Fr)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Category</label>
                      <select
                        className="form-input"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        <option value="Finance">Finance</option>
                        <option value="Personal">Personal</option>
                        <option value="Business">Business</option>
                        <option value="Travel">Travel</option>
                        <option value="Shopping">Shopping</option>
                        <option value="Online Subscriptions">Online Subscriptions</option>
                        {categories
                          .filter(
                            (c) =>
                              ![
                                'Finance',
                                'Personal',
                                'Business',
                                'Travel',
                                'Shopping',
                                'Online Subscriptions',
                              ].includes(c.name)
                          )
                          .map((c) => (
                            <option key={c.id || c.name} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  {/* Card Theme Selector */}
                  <div className="form-group" style={{ marginTop: '14px' }}>
                    <label className="form-label">Card Design Gradient</label>
                    <div className="card-theme-picker">
                      {CARD_THEMES.map((th) => (
                        <button
                          key={th.id}
                          type="button"
                          className={`card-theme-chip ${theme === th.id ? 'active' : ''}`}
                          style={{ background: th.gradient }}
                          onClick={() => setTheme(th.id)}
                          title={th.name}
                        >
                          {theme === th.id && <Check size={14} color="#fff" />}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Section 2: Card Number & Sensitive Payment Data */}
                <div className="identity-form-section">
                  <div className="identity-section-header-flex">
                    <h4 className="identity-section-heading">Payment Numbers & Security Codes</h4>
                    <span className="badge badge-primary">
                      <Lock size={12} />
                      <span>Encrypted AES-256</span>
                    </span>
                  </div>

                  <div className="grid-2-col">
                    <div className="form-group">
                      <label className="form-label">Cardholder Name (as printed on card)</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. NIKHIL SHARMA"
                        value={cardholderName}
                        onChange={(e) => setCardholderName(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Card Number
                        <span className="identity-field-subtext">
                          ({network.name} detected)
                        </span>
                      </label>
                      <div className="sensitive-input-group">
                        <input
                          type={showNumber ? 'text' : 'password'}
                          className="form-input sensitive-input"
                          placeholder="4532 •••• •••• ••••"
                          value={cardNumber}
                          onChange={handleCardNumberChange}
                          autoComplete="off"
                        />
                        <div className="sensitive-actions-cluster">
                          <button
                            type="button"
                            className="sensitive-action-btn"
                            onClick={() => setShowNumber(!showNumber)}
                            title={showNumber ? 'Hide card number' : 'Reveal card number'}
                          >
                            {showNumber ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            type="button"
                            className="sensitive-action-btn"
                            onClick={() => copyToClipboard(cardNumber, 'cardNumber')}
                            title="Copy card number"
                            disabled={!cardNumber}
                          >
                            {copiedField === 'cardNumber' ? (
                              <Check size={16} className="text-success" />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid-4-col" style={{ marginTop: '14px' }}>
                    <div className="form-group">
                      <label className="form-label">Expiry Month</label>
                      <select
                        className="form-input"
                        value={expiryMonth}
                        onChange={(e) => setExpiryMonth(e.target.value)}
                      >
                        {Array.from({ length: 12 }, (_, i) => {
                          const m = String(i + 1).padStart(2, '0');
                          return (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Expiry Year</label>
                      <select
                        className="form-input"
                        value={expiryYear}
                        onChange={(e) => setExpiryYear(e.target.value)}
                      >
                        {Array.from({ length: 20 }, (_, i) => {
                          const y = String(new Date().getFullYear() + i);
                          return (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">CVV / CVC</label>
                      <div className="sensitive-input-group">
                        <input
                          type={showCvv ? 'text' : 'password'}
                          maxLength={4}
                          className="form-input sensitive-input"
                          placeholder="•••"
                          value={cvv}
                          onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                          autoComplete="off"
                        />
                        <div className="sensitive-actions-cluster">
                          <button
                            type="button"
                            className="sensitive-action-btn"
                            onClick={() => setShowCvv(!showCvv)}
                            title={showCvv ? 'Hide CVV' : 'Reveal CVV'}
                          >
                            {showCvv ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Card PIN (Optional)</label>
                      <div className="sensitive-input-group">
                        <input
                          type={showPin ? 'text' : 'password'}
                          maxLength={6}
                          className="form-input sensitive-input"
                          placeholder="••••"
                          value={pin}
                          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                          autoComplete="off"
                        />
                        <div className="sensitive-actions-cluster">
                          <button
                            type="button"
                            className="sensitive-action-btn"
                            onClick={() => setShowPin(!showPin)}
                            title={showPin ? 'Hide PIN' : 'Reveal PIN'}
                          >
                            {showPin ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section: Linked Bank Account (Optional) */}
                <div className="identity-form-section">
                  <div className="identity-section-header-flex">
                    <h4 className="identity-section-heading">Linked Bank Account Details</h4>
                    <span className="badge badge-secondary">Optional</span>
                  </div>

                  <div className="grid-2-col">
                    <div className="form-group">
                      <label className="form-label">
                        Account Number <span className="identity-field-subtext">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. 918273645019"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                        autoComplete="off"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        IFSC Code <span className="identity-field-subtext">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. HDFC0001234, SBIN0000456"
                        value={ifscCode}
                        onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                        style={{ textTransform: 'uppercase' }}
                        maxLength={11}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Tags & Notes */}
                <div className="identity-form-section">
                  <h4 className="identity-section-heading">Tags & Confidential Notes</h4>
                  <div className="form-group">
                    <label className="form-label">Tags (Press Enter to add)</label>
                    <div className="tags-input-wrapper">
                      {tags.map((t) => (
                        <span key={t} className="tag-chip">
                          <span>#{t}</span>
                          <button
                            type="button"
                            className="tag-remove-btn"
                            onClick={() => handleRemoveTag(t)}
                            title={`Remove #${t}`}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        className="tags-bare-input"
                        placeholder={tags.length === 0 ? 'Type tag and hit Enter...' : 'Add another tag...'}
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleAddTag}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label className="form-label">Confidential Notes / Spend Perks</label>
                    <textarea
                      className="form-input identity-notes-textarea"
                      rows={4}
                      placeholder="e.g. Travel insurance perks, customer support direct line, cash back rules, rewards login..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Address Tab */}
            {activeTab === 'address' && (
              <div className="identity-form-sections">
                <div className="identity-form-section">
                  <h4 className="identity-section-heading">Card Billing Address</h4>
                  <div className="form-group">
                    <label className="form-label">Street Address</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 742 Evergreen Terrace, Apt 4B"
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                    />
                  </div>

                  <div className="grid-4-col" style={{ marginTop: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="San Francisco"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">State / Province</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="CA"
                        value={stateProvince}
                        onChange={(e) => setStateProvince(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ZIP / Postal Code</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="94107"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="United States"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Attachments Tab */}
            {activeTab === 'attachments' && (
              <div className="identity-attachments-tab-content">
                <div className="identity-attachments-header">
                  <div>
                    <h3 className="identity-section-heading">Card Scans & Attachments</h3>
                    <p className="text-secondary text-sm">
                      Upload front and back photo scans of your physical card. Files are client-side encrypted.
                    </p>
                  </div>

                  <label className="btn btn-secondary btn-sm identity-upload-btn">
                    <Paperclip size={15} />
                    <span>Upload Card Scan</span>
                    <input
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                      accept="image/*,.pdf"
                    />
                  </label>
                </div>

                {attachments.length === 0 ? (
                  <div className="identity-empty-attachments">
                    <CreditCard size={44} opacity={0.25} />
                    <p>No card scans uploaded yet.</p>
                    <span className="text-secondary text-sm">
                      Upload front & back card photos for easy digital recovery when traveling.
                    </span>
                  </div>
                ) : (
                  <div className="identity-attachments-grid">
                    {attachments.map((att) => {
                      const isImage = att.type?.startsWith('image/');
                      const sizeKb = (att.size / 1024).toFixed(1);
                      return (
                        <div key={att.id} className="identity-attachment-card">
                          <div className="identity-attachment-preview">
                            {isImage && att.dataUrl ? (
                              <img src={att.dataUrl} alt={att.name} className="identity-attachment-thumb" />
                            ) : (
                              <CreditCard size={36} className="text-primary" />
                            )}
                          </div>
                          <div className="identity-attachment-info">
                            <span className="identity-attachment-name" title={att.name}>
                              {att.name}
                            </span>
                            <span className="identity-attachment-meta">
                              {sizeKb} KB • {new Date(att.uploadedAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="identity-attachment-actions">
                            {att.dataUrl && (
                              <a
                                href={att.dataUrl}
                                download={att.name}
                                className="btn-icon btn-sm"
                                title="Download decrypted scan"
                              >
                                <Download size={14} />
                              </a>
                            )}
                            <button
                              type="button"
                              className="btn-icon btn-sm btn-icon-danger"
                              title="Delete file"
                              onClick={() => handleRemoveAttachment(att.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Security & Sharing Tab */}
            {activeTab === 'security' && (
              <div className="identity-security-tab-content">
                {/* Security PIN Lock */}
                <div className="identity-security-card">
                  <div className="identity-security-card-header">
                    <div className="identity-security-icon-box">
                      <Lock size={20} className="text-primary" />
                    </div>
                    <div className="identity-security-info">
                      <h4>Card Security Lock PIN</h4>
                      <p className="text-secondary text-sm">
                        Require an extra verification PIN before revealing full card number, CVV, and PIN.
                      </p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={isLocked}
                        onChange={(e) => setIsLocked(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {isLocked && (
                    <div className="identity-lock-pin-form">
                      <div className="form-group">
                        <label className="form-label">Set 4 to 8 Digit Security PIN</label>
                        <input
                          type="password"
                          className="form-input"
                          maxLength={8}
                          placeholder="••••"
                          value={lockPin}
                          onChange={(e) => setLockPin(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Collaboration & Sharing */}
                <div className="identity-sharing-section">
                  <h4 className="identity-section-heading">Authorized Card Collaborators</h4>
                  <p className="text-secondary text-sm" style={{ marginBottom: '16px' }}>
                    Securely share this payment card with authorized family members or team accountants.
                  </p>

                  <div className="share-add-bar">
                    <input
                      type="email"
                      className="form-input"
                      placeholder="collaborator@example.com"
                      value={shareRecipientEmail}
                      onChange={(e) => setShareRecipientEmail(e.target.value)}
                    />
                    <select
                      className="form-input"
                      style={{ width: '150px' }}
                      value={sharePermission}
                      onChange={(e) => setSharePermission(e.target.value)}
                    >
                      <option value="VIEW">Can View</option>
                      <option value="EDIT">Can Edit</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleAddShare}
                    >
                      <Plus size={15} />
                      <span>Add</span>
                    </button>
                  </div>

                  <div className="shares-list">
                    {sharesList.length === 0 ? (
                      <div className="shares-empty">
                        <Share2 size={32} opacity={0.3} />
                        <p>This payment card is private. Only you have decryption authorization.</p>
                      </div>
                    ) : (
                      <div className="shares-table">
                        {sharesList.map((sh) => (
                          <div key={sh.id} className="share-row">
                            <div className="share-email">
                              <strong>{sh.email}</strong>
                            </div>
                            <div className="share-perm-badge">
                              <span
                                className={`badge ${
                                  sh.permission === 'EDIT' ? 'badge-primary' : 'badge-secondary'
                                }`}
                              >
                                {sh.permission === 'EDIT' ? 'Can Edit' : 'Can View'}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn-icon btn-sm btn-icon-danger"
                              title="Revoke Access"
                              onClick={() => handleRemoveShare(sh.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Drawer Sticky Footer */}
          <div className="card-drawer-footer">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ fontSize: '0.8rem', padding: '7px 14px' }}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              style={{ fontSize: '0.8rem', padding: '7px 16px' }}
              disabled={saving || !hasEditPermission || !cardName.trim()}
            >
              <Save size={14} />
              <span>{saving ? 'Saving...' : 'Save Payment Card'}</span>
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
